/**
 * cadence-store.ts
 *
 * BUILD-13 Item 6: Primary write path migrated to Supabase via /api/cadence/*.
 * localStorage retained as same-session optimistic cache.
 * Backfill: bdr-backfill.ts migrates stranded local data on next login.
 *
 * 7-step cadence engine for BDR + AE.
 * Touches before Decision Gate: 2 emails + 2 phone-no-VM + 1 LinkedIn + 1 phone+VM = 6 touches.
 * Step 7 = Pursuit Decision (Marketing Hook · Park 90d · Disqualify).
 *
 * LOCKED MECHANICS:
 *  Step 1  Day 0   Email — opening
 *  Step 2  Day 2   Phone call (no voicemail)
 *  Step 3  Day 4   LinkedIn DM
 *  Step 4  Day 6   Phone call (no voicemail)
 *  Step 5  Day 9   Email — follow-up
 *  Step 6  Day 12  Phone + acknowledgment voicemail
 *  Step 7  Day 14+ DECISION GATE (manager/AE picks Marketing Hook | Park | Disqualify)
 *
 * The same cadence applies to AEs post-handoff — they restart at Step 1 with a
 * discovery-themed conversation. owner_role distinguishes ('bdr' vs 'ae').
 */

export type CadenceStepId = 1 | 2 | 3 | 4 | 5 | 6 | 7;

export type CadenceActionKind =
  | "email_opening"
  | "phone_no_vm"
  | "linkedin_dm"
  | "email_followup"
  | "phone_with_vm"
  | "decision_gate";

export interface CadenceStepDef {
  id: CadenceStepId;
  day_offset: number;        // days from Day 0
  kind: CadenceActionKind;
  label: string;
  short: string;             // 1-line action prompt
  script_hint?: string;      // tooltip / coaching hint
}

export const CADENCE_PROGRAM: CadenceStepDef[] = [
  { id: 1, day_offset: 0,  kind: "email_opening", label: "Email — opening",
    short: "Send opening email",
    script_hint: "Personalized intro with hook. Lead with the prospect's situation, not your product." },
  { id: 2, day_offset: 2,  kind: "phone_no_vm", label: "Phone call — no voicemail",
    short: "Call · hang up if no answer",
    script_hint: "First attempt. If they don't pick up, hang up. No voicemail." },
  { id: 3, day_offset: 4,  kind: "linkedin_dm", label: "LinkedIn DM",
    short: "Send LinkedIn DM",
    script_hint: "Reference the email you sent. 'Wanted to connect here too — sent you a note Monday.'" },
  { id: 4, day_offset: 6,  kind: "phone_no_vm", label: "Phone call — no voicemail",
    short: "Second call · no voicemail",
    script_hint: "Second attempt. Still no voicemail — hang up if no answer." },
  { id: 5, day_offset: 9,  kind: "email_followup", label: "Email — follow-up",
    short: "Send follow-up email",
    script_hint: "Different angle than opener. Value drop, reference of prior touches." },
  { id: 6, day_offset: 12, kind: "phone_with_vm", label: "Phone + acknowledgment voicemail",
    short: "Call · leave acknowledgment voicemail",
    script_hint: 'VM: "Hi [Name], this is [BDR] from Mi6 Group. I know you\'re slammed — no need to call me back. I\'ll follow up with you in the next couple weeks. Wanted to keep myself on your radar in case [hook reason]. Have a good one."' },
  { id: 7, day_offset: 14, kind: "decision_gate", label: "Pursuit Decision",
    short: "Decide: Marketing Hook · Park · Disqualify",
    script_hint: "End of standard cadence. Choose: invest in a marketing hook (event invite, mailer, custom asset), park 90 days, or disqualify." },
];

export type ProspectStatus =
  | "active"           // in cadence
  | "paused"           // park 90d
  | "disqualified"
  | "marketing_hook"   // routed to marketing
  | "converted";       // moved to AE / opportunity

export type ProspectSource =
  | "marketing_mql"    // came from a marketing-driven marketing channel
  | "cold_outbound"    // BDR/AE self-sourced cold
  | "event"            // tradeshow / round table / webinar
  | "referral"         // warm intro
  | "manager_assigned"; // assigned by manager from strategic list

export interface CadenceProspect {
  id: string;
  owner_id: string;            // BDR or AE user id — currently working it
  owner_role: "bdr" | "ae";
  sourced_by_id: string;       // who originally sourced — same as owner_id when self-sourced
  sourced_by_role: "bdr" | "ae" | "marketing"; // marketing-sourced when MQL
  full_name: string;
  title: string;
  company: string;
  email?: string;
  phone?: string;
  linkedin?: string;
  source: ProspectSource;
  status: ProspectStatus;
  current_step: CadenceStepId;     // next step due
  enrolled_at: string;             // ISO date — Day 0
  next_action_at: string;          // ISO date — when current_step is due
  last_action_at?: string;         // ISO date — when last step was completed
  notes?: string;
  marketing_hook_kind?: string;    // if status=marketing_hook
}

export interface CadenceAction {
  id: string;
  prospect_id: string;
  owner_id: string;
  step: CadenceStepId;
  kind: CadenceActionKind;
  executed_at: string;             // ISO timestamp
  outcome?: "no_answer" | "left_vm" | "connected" | "replied" | "bounced" | "no_response";
  note?: string;
}

// ─── Storage keys ─────────────────────────────────────────────────────────────

const KEY_PROSPECTS = "aip:cadence:prospects:v1";
const KEY_ACTIONS   = "aip:cadence:actions:v1";

// ─── Read / write helpers ─────────────────────────────────────────────────────

// BUILD-13 Item 6: api import for Supabase sync
import { api } from "@/lib/session";
import { logActivity } from "./activity-log";

// ─── Cache helpers ─────────────────────────────────────────────────────────────

function cacheProspects(list: CadenceProspect[]): void {
  try { localStorage.setItem(KEY_PROSPECTS, JSON.stringify(list)); } catch { /* quota */ }
}

function cacheActions(list: CadenceAction[]): void {
  try { localStorage.setItem(KEY_ACTIONS, JSON.stringify(list)); } catch { /* quota */ }
}

export function loadProspects(): CadenceProspect[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(KEY_PROSPECTS);
    const cached: CadenceProspect[] = raw ? JSON.parse(raw) : [];
    // Background refresh from server (non-blocking)
    void api("/api/cadence/prospects").then((data: any) => {
      const list = (data?.prospects ?? []) as CadenceProspect[];
      cacheProspects(list);
      window.dispatchEvent(new CustomEvent("aip:cadence-prospects-refreshed", { detail: list }));
    }).catch(() => { /* ignore — cache serves as fallback */ });
    return cached;
  } catch { return []; }
}

export function saveProspects(list: CadenceProspect[]) {
  if (typeof window === "undefined") return;
  cacheProspects(list);
  // Fire-and-forget server sync
  void api("/api/cadence/prospects/bulk", {
    method: "POST",
    body: JSON.stringify({ prospects: list }),
  }).catch((err: any) =>
    console.error("[cadence-store] saveProspects server sync failed:", err?.message)
  );
}

export function loadActions(): CadenceAction[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(KEY_ACTIONS);
    const cached: CadenceAction[] = raw ? JSON.parse(raw) : [];
    void api("/api/cadence/actions").then((data: any) => {
      const list = (data?.actions ?? []) as CadenceAction[];
      cacheActions(list);
      window.dispatchEvent(new CustomEvent("aip:cadence-actions-refreshed", { detail: list }));
    }).catch(() => { /* ignore */ });
    return cached;
  } catch { return []; }
}

export function saveActions(list: CadenceAction[]) {
  if (typeof window === "undefined") return;
  cacheActions(list);
  void api("/api/cadence/actions/bulk", {
    method: "POST",
    body: JSON.stringify({ actions: list }),
  }).catch((err: any) =>
    console.error("[cadence-store] saveActions server sync failed:", err?.message)
  );
}

// ─── Date helpers ─────────────────────────────────────────────────────────────

function toISODate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function addDays(iso: string, days: number): string {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + days);
  return toISODate(d);
}

function todayISO(): string {
  return toISODate(new Date());
}

// ─── Enrollment + progression ─────────────────────────────────────────────────

export interface EnrollInput {
  owner_id: string;
  owner_role: "bdr" | "ae";
  sourced_by_id?: string;                       // defaults to owner_id (self-sourced)
  sourced_by_role?: "bdr" | "ae" | "marketing"; // defaults to owner_role
  full_name: string;
  title: string;
  company: string;
  email?: string;
  phone?: string;
  linkedin?: string;
  source: ProspectSource;
  enrolled_at?: string;            // defaults to today
  notes?: string;
}

export function enrollProspect(input: EnrollInput): CadenceProspect {
  const day0 = input.enrolled_at ?? todayISO();
  const prospect: CadenceProspect = {
    id: `p_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    owner_id: input.owner_id,
    owner_role: input.owner_role,
    sourced_by_id: input.sourced_by_id ?? input.owner_id,
    sourced_by_role: input.sourced_by_role ?? input.owner_role,
    full_name: input.full_name,
    title: input.title,
    company: input.company,
    email: input.email,
    phone: input.phone,
    linkedin: input.linkedin,
    source: input.source,
    status: "active",
    current_step: 1,
    enrolled_at: day0,
    next_action_at: day0,                            // Step 1 due Day 0
    notes: input.notes,
  };
  const list = loadProspects();
  list.push(prospect);
  saveProspects(list);

  void logActivity({
    action: "cadence.enroll",
    entity_type: "cadence_enrollment",
    entity_id: prospect.id,
    payload: {
      full_name: input.full_name,
      company: input.company,
      source: input.source,
    },
  });
  return prospect;
}

/**
 * Mark a step complete. Auto-advances current_step + next_action_at.
 * If step 6 is completed, advances to step 7 (decision gate).
 * If step 7 (decision gate) is resolved separately via setStatus().
 */
export function completeStep(
  prospect_id: string,
  step: CadenceStepId,
  outcome?: CadenceAction["outcome"],
  note?: string
): { prospect: CadenceProspect | null; action: CadenceAction | null } {
  const prospects = loadProspects();
  const p = prospects.find((x) => x.id === prospect_id);
  if (!p) return { prospect: null, action: null };

  const def = CADENCE_PROGRAM.find((s) => s.id === step);
  if (!def) return { prospect: null, action: null };

  const now = new Date().toISOString();

  const action: CadenceAction = {
    id: `a_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    prospect_id,
    owner_id: p.owner_id,
    step,
    kind: def.kind,
    executed_at: now,
    outcome,
    note,
  };
  const actions = loadActions();
  actions.push(action);
  saveActions(actions);

  // Advance the prospect to the next step
  const next = (step + 1) as CadenceStepId;
  if (next > 7) {
    p.current_step = 7;
  } else {
    p.current_step = next;
    const nextDef = CADENCE_PROGRAM.find((s) => s.id === next);
    if (nextDef) {
      p.next_action_at = addDays(p.enrolled_at, nextDef.day_offset);
    }
  }
  p.last_action_at = now;
  saveProspects(prospects);

  void logActivity({
    action: "cadence.step.complete",
    entity_type: "cadence_enrollment",
    entity_id: prospect_id,
    payload: {
      step_number: step,
      outcome: outcome ?? null,
      has_notes: Boolean(note),
    },
  });

  return { prospect: p, action };
}

export function setProspectStatus(
  prospect_id: string,
  status: ProspectStatus,
  hookKind?: string
): CadenceProspect | null {
  const prospects = loadProspects();
  const p = prospects.find((x) => x.id === prospect_id);
  if (!p) return null;
  p.status = status;
  if (status === "marketing_hook" && hookKind) {
    p.marketing_hook_kind = hookKind;
  }
  if (status === "paused") {
    p.next_action_at = addDays(todayISO(), 90);
  }
  saveProspects(prospects);
  return p;
}

// ─── Daily queue builder ──────────────────────────────────────────────────────

export interface DailyQueueGroup {
  step: CadenceStepId;
  def: CadenceStepDef;
  prospects: CadenceProspect[];
}

/**
 * Returns prospects grouped by step, filtered by owner.
 * Includes prospects DUE today or OVERDUE (past-due flagged).
 *
 * Filters:
 *   - status === 'active'
 *   - next_action_at <= today
 */
export function getDailyQueue(owner_id: string): {
  total: number;
  today: string;
  overdueCount: number;
  groups: DailyQueueGroup[];
} {
  const today = todayISO();
  const all = loadProspects().filter(
    (p) => p.owner_id === owner_id && p.status === "active" && p.next_action_at <= today
  );

  const groups: DailyQueueGroup[] = CADENCE_PROGRAM.map((def) => ({
    step: def.id,
    def,
    prospects: all.filter((p) => p.current_step === def.id),
  })).filter((g) => g.prospects.length > 0);

  const overdueCount = all.filter((p) => p.next_action_at < today).length;

  return { total: all.length, today, overdueCount, groups };
}

// ─── Configurable cadence programs ──────────────────────────────────────────

export type CadenceActionType =
  | "email_opening"
  | "email_followup"
  | "phone_no_vm"
  | "phone_with_vm"
  | "linkedin_dm"
  | "sms"
  | "video_message"
  | "direct_mail"
  | "decision_gate";

export const ACTION_TYPE_LABELS: Record<CadenceActionType, string> = {
  email_opening: "Email — Opening",
  email_followup: "Email — Follow-up",
  phone_no_vm: "Phone — No Voicemail",
  phone_with_vm: "Phone — With Voicemail",
  linkedin_dm: "LinkedIn DM",
  sms: "SMS",
  video_message: "Video Message",
  direct_mail: "Direct Mail",
  decision_gate: "Pursuit Decision",
};

export interface ConfigurableCadenceStep {
  id: string;
  position: number;        // 1–10
  day_offset: number;
  action_type: CadenceActionType;
  label: string;
  script_id?: string;      // FK to template-library script
  custom_script?: string;  // free-form override
  hint?: string;
}

export interface CadenceProgram {
  id: string;
  name: string;
  owner_id: string;        // manager who built it
  team_id?: string;
  steps: ConfigurableCadenceStep[];
  version: number;
  published_at: string;
  is_default: boolean;
}

const KEY_PROGRAMS = "aip:cadence:programs:v1";
const KEY_ACTIVE_PROGRAM = "aip:cadence:active_program:v1";

const DEFAULT_MI6_PROGRAM: CadenceProgram = {
  id: "default_mi6",
  name: "Mi6 Standard Cadence",
  owner_id: "system",
  steps: [
    { id: "s1", position: 1, day_offset: 0,  action_type: "email_opening",  label: "Cadence Step 1 — Opening Email" },
    { id: "s2", position: 2, day_offset: 2,  action_type: "phone_no_vm",    label: "Cadence Step 2 — Call (No Voicemail)" },
    { id: "s3", position: 3, day_offset: 4,  action_type: "linkedin_dm",    label: "Cadence Step 3 — LinkedIn DM" },
    { id: "s4", position: 4, day_offset: 6,  action_type: "phone_no_vm",    label: "Cadence Step 4 — Second Call (No Voicemail)" },
    { id: "s5", position: 5, day_offset: 9,  action_type: "email_followup", label: "Cadence Step 5 — Follow-up Email" },
    { id: "s6", position: 6, day_offset: 12, action_type: "phone_with_vm",  label: "Cadence Step 6 — Call + Voicemail" },
    { id: "s7", position: 7, day_offset: 14, action_type: "decision_gate",  label: "Cadence Step 7 — Pursuit Decision" },
  ],
  version: 1,
  published_at: new Date().toISOString().slice(0, 10),
  is_default: true,
};

export function loadPrograms(): CadenceProgram[] {
  if (typeof window === "undefined") return [DEFAULT_MI6_PROGRAM];
  try {
    const raw = localStorage.getItem(KEY_PROGRAMS);
    const stored: CadenceProgram[] = raw ? JSON.parse(raw) : [];
    const hasDefault = stored.some((p) => p.id === DEFAULT_MI6_PROGRAM.id);
    const cached = hasDefault ? stored : [DEFAULT_MI6_PROGRAM, ...stored];
    // Background refresh from server
    void api("/api/cadence/programs").then((data: any) => {
      const list = (data?.programs ?? [DEFAULT_MI6_PROGRAM]) as CadenceProgram[];
      try { localStorage.setItem(KEY_PROGRAMS, JSON.stringify(list)); } catch { /* quota */ }
      window.dispatchEvent(new CustomEvent("aip:cadence-programs-refreshed", { detail: list }));
    }).catch(() => { /* ignore */ });
    return cached;
  } catch {
    return [DEFAULT_MI6_PROGRAM];
  }
}

export function savePrograms(list: CadenceProgram[]) {
  if (typeof window === "undefined") return;
  try { localStorage.setItem(KEY_PROGRAMS, JSON.stringify(list)); } catch { /* quota */ }
  // BUILD-13 Item 6: sync to server
  void api("/api/cadence/programs/bulk", {
    method: "PUT",
    body: JSON.stringify({ programs: list }),
  }).catch((err: any) =>
    console.error("[cadence-store] savePrograms server sync failed:", err?.message)
  );
}

export function getActiveProgram(): CadenceProgram {
  if (typeof window === "undefined") return DEFAULT_MI6_PROGRAM;
  try {
    const activeId = localStorage.getItem(KEY_ACTIVE_PROGRAM);
    if (activeId) {
      const programs = loadPrograms();
      const found = programs.find((p) => p.id === activeId);
      if (found) return found;
    }
    const programs = loadPrograms();
    return programs.find((p) => p.is_default) ?? DEFAULT_MI6_PROGRAM;
  } catch {
    return DEFAULT_MI6_PROGRAM;
  }
}

export function setActiveProgram(id: string) {
  if (typeof window === "undefined") return;
  try { localStorage.setItem(KEY_ACTIVE_PROGRAM, id); } catch { /* quota */ }
  // BUILD-13 Item 6: persist pref to server
  void api("/api/cadence/programs/active", {
    method: "PUT",
    body: JSON.stringify({ program_id: id }),
  }).catch((err: any) =>
    console.error("[cadence-store] setActiveProgram server sync failed:", err?.message)
  );
}

export function publishProgram(program: CadenceProgram): CadenceProgram {
  const updated: CadenceProgram = {
    ...program,
    version: program.version + 1,
    published_at: new Date().toISOString().slice(0, 10),
  };

  const programs = loadPrograms();
  const idx = programs.findIndex((p) => p.id === updated.id);
  if (idx >= 0) {
    programs[idx] = updated;
  } else {
    programs.push(updated);
  }
  savePrograms(programs);

  // Migrate all active prospects: recalculate next_action_at based on new step day_offsets
  const prospects = loadProspects();
  const changed = prospects.map((p) => {
    if (p.status !== "active") return p;
    // Map current numeric step (1–7) to the configurable step by position
    const matchingStep = updated.steps.find((s) => s.position === p.current_step);
    if (!matchingStep) return p;
    const newNextAction = addDays(p.enrolled_at, matchingStep.day_offset);
    return { ...p, next_action_at: newNextAction };
  });
  saveProspects(changed);

  return updated;
}

export function getStepsForActiveProgram(): ConfigurableCadenceStep[] {
  return getActiveProgram().steps.slice().sort((a, b) => a.position - b.position);
}

// ─── Daily counters auto-roll ─────────────────────────────────────────────────

/**
 * Returns today's counter rollups from completed actions for a given owner.
 * Used to auto-fill the BDR/AE daily activity log.
 */
export function getTodayCounters(owner_id: string): {
  emails_sent: number;
  dials_made: number;
  voicemails_left: number;
  no_message_hangups: number;
  linkedin_dms: number;
} {
  const today = todayISO();
  const actions = loadActions().filter(
    (a) => a.owner_id === owner_id && a.executed_at.slice(0, 10) === today
  );

  let emails_sent = 0;
  let dials_made = 0;
  let voicemails_left = 0;
  let no_message_hangups = 0;
  let linkedin_dms = 0;

  for (const a of actions) {
    switch (a.kind) {
      case "email_opening":
      case "email_followup":
        emails_sent += 1;
        break;
      case "phone_no_vm":
        dials_made += 1;
        no_message_hangups += 1;
        break;
      case "phone_with_vm":
        dials_made += 1;
        voicemails_left += 1;
        break;
      case "linkedin_dm":
        linkedin_dms += 1;
        break;
    }
  }

  return { emails_sent, dials_made, voicemails_left, no_message_hangups, linkedin_dms };
}

// ─── Demo seed (so Michael sees a queue immediately) ──────────────────────────

export function seedDemoProspectsIfEmpty(owner_id: string, owner_role: "bdr" | "ae" = "bdr") {
  const existing = loadProspects();
  if (existing.some((p) => p.owner_id === owner_id)) return;

  const today = todayISO();
  // sourced_by_id / sourced_by_role default to owner for demo seeds
  const ds = (id: string, source: ProspectSource, extra: Partial<CadenceProspect>): CadenceProspect => ({
    id, owner_id, owner_role,
    sourced_by_id: owner_id,
    sourced_by_role: owner_role,
    full_name: "", title: "", company: "", source, status: "active", current_step: 1,
    enrolled_at: today, next_action_at: today,
    ...extra,
  });
  const seed: CadenceProspect[] = [
    // Step 1 due today (4 prospects)
    ds("demo_1", "manager_assigned", { full_name: "Sarah Chen", title: "COO", company: "Acme Manufacturing",
      email: "schen@acme.example" }),
    ds("demo_2", "marketing_mql", { full_name: "Mike Lopez", title: "VP Operations", company: "Beta Industries",
      email: "mlopez@beta.example" }),
    { id: "demo_3", owner_id, owner_role, sourced_by_id: owner_id, sourced_by_role: owner_role,
      full_name: "Linda Park", title: "CFO", company: "Gamma Corp",
      email: "lpark@gamma.example", source: "cold_outbound", status: "active", current_step: 1,
      enrolled_at: today, next_action_at: today },
    ds("demo_4", "event", { full_name: "Tom Reed", title: "Director of Procurement", company: "Delta LLC",
      email: "treed@delta.example" }),

    // Step 2 due today (3 prospects)
    ds("demo_5", "marketing_mql", { full_name: "Pat Singh", title: "VP Engineering", company: "Echo Manufacturing",
      phone: "+1-555-0101", current_step: 2,
      enrolled_at: addDays(today, -2), next_action_at: today, last_action_at: addDays(today, -2) + "T15:00:00Z" }),
    ds("demo_6", "cold_outbound", { full_name: "Jen Liu", title: "COO", company: "Foxtrot Systems",
      phone: "+1-555-0102", current_step: 2,
      enrolled_at: addDays(today, -2), next_action_at: today, last_action_at: addDays(today, -2) + "T16:00:00Z" }),
    ds("demo_7", "manager_assigned", { full_name: "Robert Diaz", title: "President", company: "Golf Industries",
      phone: "+1-555-0103", current_step: 2,
      enrolled_at: addDays(today, -3), next_action_at: addDays(today, -1), last_action_at: addDays(today, -3) + "T14:00:00Z" }),

    // Step 3 due today (2 prospects)
    ds("demo_8", "referral", { full_name: "Karen Yu", title: "VP Strategy", company: "Hotel Holdings Mfg",
      linkedin: "linkedin.com/in/kyu", current_step: 3,
      enrolled_at: addDays(today, -4), next_action_at: today, last_action_at: addDays(today, -2) + "T11:00:00Z" }),
    ds("demo_9", "marketing_mql", { full_name: "David Park", title: "CFO", company: "India Industrial",
      linkedin: "linkedin.com/in/dpark", current_step: 3,
      enrolled_at: addDays(today, -4), next_action_at: today, last_action_at: addDays(today, -2) + "T13:00:00Z" }),

    // Step 5 due today (1 prospect)
    ds("demo_10", "cold_outbound", { full_name: "Maya Patel", title: "VP Procurement", company: "Juliet Manufacturing",
      email: "mpatel@juliet.example", current_step: 5,
      enrolled_at: addDays(today, -9), next_action_at: today, last_action_at: addDays(today, -3) + "T10:00:00Z" }),

    // Step 7 — decision gate (3 prospects)
    ds("demo_11", "manager_assigned", { full_name: "Alex Murphy", title: "COO", company: "Kilo Corporation",
      email: "amurphy@kilo.example", current_step: 7,
      enrolled_at: addDays(today, -14), next_action_at: today, last_action_at: addDays(today, -2) + "T09:00:00Z",
      notes: "Strategic Top-10 target. High-value if we land it." }),
    ds("demo_12", "marketing_mql", { full_name: "Nina Walsh", title: "VP Finance", company: "Lima Industries",
      email: "nwalsh@lima.example", current_step: 7,
      enrolled_at: addDays(today, -15), next_action_at: addDays(today, -1), last_action_at: addDays(today, -3) + "T11:00:00Z" }),
    ds("demo_13", "event", { full_name: "Greg Owens", title: "Director Ops", company: "Mike Manufacturing",
      email: "gowens@mike.example", current_step: 7,
      enrolled_at: addDays(today, -14), next_action_at: today, last_action_at: addDays(today, -2) + "T15:00:00Z",
      notes: "Met at Manufacturing Tech Summit booth — promised follow-up." }),
  ];

  saveProspects([...existing, ...seed]);
}
